import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { portalAuthApi, portalActingApi } from '../services/portalApi';

const PortalAuthContext = createContext(null);

const portalTrustKey = (email) => `portalMfaTrusted:${(email || '').toLowerCase()}`;

export const usePortalAuth = () => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within PortalAuthProvider');
  }
  return context;
};

export const PortalAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSudo, setIsSudo] = useState(!!localStorage.getItem('portalSudoToken'));
  // Acts-on-behalf-of mandates: the companies this person may work on besides
  // their own, and who has been given authority over their own (v1.289.0).
  const [actingFor, setActingFor] = useState([]);
  const [agentsActingForUs, setAgentsActingForUs] = useState([]);

  const loadActing = useCallback(async () => {
    try {
      const { data } = await portalActingApi.get();
      setActingFor(data.actingFor || []);
      setAgentsActingForUs(data.agentsActingForUs || []);
    } catch {
      setActingFor([]);
      setAgentsActingForUs([]);
    }
  }, []);

  useEffect(() => {
    if (user) loadActing();
  }, [user, loadActing]);

  useEffect(() => {
    const checkAuth = async () => {
      // "View Portal As This User" opens /portal?sudoToken=… — pick the token up,
      // store it, and clean it out of the address bar. Nothing read this before,
      // so the read-only session always bounced straight back to the login page.
      const params = new URLSearchParams(window.location.search);
      const sudoToken = params.get('sudoToken');
      if (sudoToken) {
        localStorage.setItem('portalAccessToken', sudoToken);
        localStorage.setItem('portalSudoToken', sudoToken);
        localStorage.removeItem('portalRefreshToken');
        setIsSudo(true);
        params.delete('sudoToken');
        const clean = window.location.pathname + (params.toString() ? `?${params}` : '');
        window.history.replaceState({}, '', clean);
        try {
          const { data } = await portalAuthApi.me();
          setUser(data);
          localStorage.setItem('portalUser', JSON.stringify(data));
        } catch {
          clearAuth();
        }
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('portalAccessToken');
      const saved = localStorage.getItem('portalUser');
      if (token && saved) {
        try {
          const { data } = await portalAuthApi.me();
          setUser(data);
          localStorage.setItem('portalUser', JSON.stringify(data));
        } catch {
          // Try refresh
          const refresh = localStorage.getItem('portalRefreshToken');
          if (refresh) {
            try {
              const { data: refreshData } = await portalAuthApi.refresh(refresh);
              localStorage.setItem('portalAccessToken', refreshData.accessToken);
              localStorage.setItem('portalRefreshToken', refreshData.refreshToken);
              const { data: meData } = await portalAuthApi.me();
              setUser(meData);
              localStorage.setItem('portalUser', JSON.stringify(meData));
            } catch {
              clearAuth();
            }
          } else {
            clearAuth();
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const clearAuth = () => {
    localStorage.removeItem('portalAccessToken');
    localStorage.removeItem('portalRefreshToken');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalSudoToken');
    setIsSudo(false);
    setUser(null);
  };

  const storeSession = useCallback((data, email) => {
    if (data.trustedDeviceToken && email) localStorage.setItem(portalTrustKey(email), data.trustedDeviceToken);
    localStorage.setItem('portalAccessToken', data.accessToken);
    localStorage.setItem('portalRefreshToken', data.refreshToken);
    localStorage.setItem('portalUser', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  // Resolves to the user, or to { mfa } when the emailed code must come first.
  const login = useCallback(async (email, password) => {
    const { data } = await portalAuthApi.login(email, password, localStorage.getItem(portalTrustKey(email)) || undefined);
    if (data.mfaRequired) return { mfa: data };
    return storeSession(data, email);
  }, [storeSession]);

  const logout = useCallback(async () => {
    try {
      const refresh = localStorage.getItem('portalRefreshToken');
      if (refresh) await portalAuthApi.logout(refresh);
    } catch { /* ignore */ }
    clearAuth();
  }, []);

  // A "view as" session is read-only (the backend refuses writes too) — hide the
  // controls rather than offer a button that will be rejected.
  const WRITE_ACTIONS = ['create', 'reply', 'changeStatus'];

  const hasPermission = useCallback((resource, action) => {
    if (!user?.portalPermissions) return false;
    if (isSudo && (WRITE_ACTIONS.includes(action) || resource === 'manageUsers')) return false;
    const perms = user.portalPermissions[resource];
    if (typeof perms === 'boolean') return perms;
    if (perms?.[action]) return true;
    // A mandate carries its own permission set for the company it covers, so a
    // control must appear if EITHER their own company or one they act for allows it.
    return actingFor.some(m => {
      const granted = m.permissions?.[resource];
      return typeof granted === 'boolean' ? granted : !!granted?.[action];
    });
  }, [user, isSudo, actingFor]);

  return (
    <PortalAuthContext.Provider value={{
      user,
      loading,
      login,
      completeLogin: storeSession,
      logout,
      hasPermission,
      isAuthenticated: !!user,
      isSudo,
      actingFor,
      agentsActingForUs,
      refreshActing: loadActing,
    }}>
      {children}
    </PortalAuthContext.Provider>
  );
};
